"use client"

import { t } from "@/languages/i18n"
import { MediaData } from "@/lib/config/types"
import getDomains from "@/services/domain-services"
import { DomainEntity } from "@/types/domain"
import { EditOutlined } from "@ant-design/icons"
import { Button, Form, Input, Select } from "antd"
import React, { useEffect, useState } from "react"
import FeaturedImage from "../../post/pages/FeaturedImage"

export interface CategoryFormValues {
  name: string
  slug: string
  description: string
  domain: string
  type: "post" | "video" | ""
  playlistUrl: string
}

interface CategoryFormFieldsProps {
  form: any
  isEditMode: boolean
  isEditableSlug: boolean
  onSlugToggle: () => void
  onNameBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  bannerMedia?: MediaData | null
  setBannerMedia?: (media: MediaData | null) => void
}

const CategoryFormFields: React.FC<CategoryFormFieldsProps> = ({
  form,
  isEditMode,
  isEditableSlug,
  onSlugToggle,
  onNameBlur,
  bannerMedia,
  setBannerMedia,
}) => {
  const [domains, setDomains] = useState<Array<{ id: string; name: string }>>([])

  const loadDomains = async () => {
    try {
      const response = await getDomains()
      if (response) {
        const domainOptions = response.map((domain: DomainEntity) => ({
          id: domain.id.toString(),
          name: domain.attributes?.name || `Domain ${domain.id}`,
        }))
        setDomains(domainOptions)
      }
    } catch (error) {
      console.error("Failed to load domains:", error)
    }
  }

  useEffect(() => {
    loadDomains()
  }, [])

  return (
    <>
      <Form.Item
        className="w-full"
        style={{ marginBottom: 20, width: "100%" }}
        name="name"
        label={<span className="text-base font-medium">{t("Name")}</span>}
        rules={[
          {
            required: true,
            message: t("Vui lòng nhập tên danh mục"),
          },
        ]}
        extra={t("The name is how it appears on your site.")}
      >
        <Input style={isEditMode ? { width: "100%" } : undefined} onBlur={onNameBlur} />
      </Form.Item>

      {!isEditMode ? (
        <div className="flex w-full relative">
          <Form.Item
            className="w-full"
            name="slug"
            label={<span className="text-base font-medium">{t("Slug")}</span>}
            rules={[{ required: true, message: t("Không được để trống!") }]}
            extra={t("May not need to be entered (automatically render by name)")}
          >
            <Input name="slug" className="w-[80%]" disabled={isEditableSlug} />
          </Form.Item>
          <Button
            className="absolute right-0 py-1 px-2"
            icon={<EditOutlined />}
            color={!isEditableSlug ? "primary" : "default"}
            variant="outlined"
            onClick={onSlugToggle}
          >
            {t("Edit")}
          </Button>
        </div>
      ) : (
        <Form.Item
          style={{ marginBottom: 20 }}
          name="slug"
          label={<span className="text-base font-medium">{t("Slug")}</span>}
          rules={[{ required: true, message: t("Không được để trống!") }]}
          extra={t(
            "The “slug” is the URL-friendly version of the name. It is usually all lowercase and contains only letters, numbers, and hyphens.",
          )}
        >
          <Input />
        </Form.Item>
      )}

      <Form.Item
        className="w-full description"
        name="description"
        label={<span className="text-base font-medium">{t("Description")}</span>}
        extra={t("The description is not prominent by default; however, some themes may show it.")}
      >
        <Input.TextArea rows={6} className="w-full" />
      </Form.Item>

      <Form.Item
        className="w-full"
        name="domain"
        label={<span className="text-base font-medium">{t("Domain")}</span>}
        extra={t("Chọn tên miền cho danh mục này")}
      >
        <Select placeholder={t("Select domain")} allowClear style={{ width: "100%" }}>
          {domains.map(domain => (
            <Select.Option key={domain.id} value={domain.name}>
              {domain.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className="w-full"
        name="type"
        label={<span className="text-base font-medium">{t("Type")}</span>}
        extra={t("Chọn hình thức nội dung cho danh mục này")}
      >
        <Select placeholder={t("Select type")} allowClear style={{ width: "100%" }}>
          <Select.Option value="post">{t("Post")}</Select.Option>
          <Select.Option value="video">{t("Video")}</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        className="w-full"
        name="playlistUrl"
        label={<span className="text-base font-medium">{t("Playlist URL")}</span>}
        extra={t("Điền URL của danh sách phát cho danh mục này (không bắt buộc).")}
      >
        <Input placeholder={t("Enter playlist URL")} style={{ width: "100%" }} />
      </Form.Item>

      {setBannerMedia && (
        <Form.Item
          className="w-full"
          label={<span className="text-base font-medium">{t("Banner")}</span>}
          extra={t("Ảnh banner hiển thị đầu trang bài viết (áp dụng cho Tamda Foods).")}
        >
          <div className="max-w-[700px]">
            <FeaturedImage selectedMedia={bannerMedia ?? null} setSelectedMedia={setBannerMedia} />
          </div>
        </Form.Item>
      )}
    </>
  )
}

export default CategoryFormFields
